import { QuoteLineItemProps } from '@partsbadger/types';

export const CUSTOM_OPTION_REQUIRED_MESSAGE = 'Custom name is required';

type ProductRecord = Record<string, unknown>;

interface CustomOptionConfig {
    selectedValues: unknown[];
    customValue: unknown;
}

const trimValue = (value: unknown) => (value == null ? '' : String(value).trim());

const getOptionName = (value: unknown): string => {
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null && 'name' in value) {
        return trimValue((value as { name?: unknown }).name);
    }

    return '';
};

const getFirstOptionName = (value: unknown): string => {
    if (!Array.isArray(value) || value.length === 0) return '';
    return getOptionName(value[0]);
};

const getProduct = (item?: QuoteLineItemProps | null): ProductRecord | null => {
    return (item?.product as ProductRecord | undefined) ?? null;
};

const hasMissingCustomValue = ({ selectedValues, customValue }: CustomOptionConfig): boolean => {
    const selectedNames = selectedValues.map(getOptionName).filter(Boolean);
    return selectedNames.includes('Custom') && !trimValue(customValue);
};

export const isCustomMaterialMissing = (item?: QuoteLineItemProps | null): boolean => {
    const product = getProduct(item);
    if (!product) return false;

    return hasMissingCustomValue({
        selectedValues: [item?.material, product.material_related, product.material],
        customValue: product.custom_material,
    });
};

export const isCustomSurfaceFinishMissing = (item?: QuoteLineItemProps | null): boolean => {
    const product = getProduct(item);
    if (!product) return false;

    return hasMissingCustomValue({
        selectedValues: [item?.finish, product.finish_related, getFirstOptionName(product.finishes)],
        customValue: product.custom_finish,
    });
};

export const isCustomCoatingMissing = (item?: QuoteLineItemProps | null): boolean => {
    const product = getProduct(item);
    if (!product) return false;

    return hasMissingCustomValue({
        selectedValues: [item?.coating, product.coating_related, getFirstOptionName(product.coatings)],
        customValue: product.custom_coating,
    });
};

export const isAnyCustomOptionMissing = (item?: QuoteLineItemProps | null): boolean => {
    return isCustomMaterialMissing(item) || isCustomSurfaceFinishMissing(item) || isCustomCoatingMissing(item);
};

export const isConfiguredPartComplete = (part?: QuoteLineItemProps | null): boolean => {
    const hasRequiredSelections = Boolean(part?.manufacturing_type && part?.material && part?.finish && part?.coating);
    return hasRequiredSelections && !isAnyCustomOptionMissing(part);
};
